import {DateTime} from 'luxon';

import {CLSelectorOption} from 'common/src/components/Selectors/CLSelector/CLSelector.types';
import {LandingPageSource} from '../campaigns/campaigns.enums';

enum SocialPlatform {
  TikTok = 'TikTok',
  Facebook = 'Facebook',
  Youtube = 'Youtube',
  Snapchat = 'Snapchat',
  LinkedIn = 'LinkedIn',
  GoogleAds = 'GoogleAds',
  Pinterest = 'Pinterest',
  TheTradeDesk = 'TheTradeDesk',
  DoubleVerify = 'DoubleVerify',
  Foursquare = 'Foursquare',
  DV360 = 'DV360',
  IAS = 'IAS',
  Neustar = 'Neustar',
  Merkury = 'MerkuryAudiences',
  LiveRamp = 'LiverampFbr',
  AmazonAds = 'Amazon',
  MockSocial = 'MockSocial',
}

enum SocialAssetTypeName {
  fbAdAccount = 'FB_AdAccount',
  youtubeChannel = 'Youtube_Channel',
  tiktokAccount = 'Tiktok_AdAccount',
  snapchatAccount = 'Snapchat_AdAccount',
  linkedinAccount = 'LinkedIn_AdAccount',
  theTradeDesk = 'TheTradeDesk_Advertiser',
  doubleVerify = 'DoubleVerify_Account',
  fourSquare = 'Foursquare_Integration',
  googleAdsAccount = 'Google_AdAccount',
  dv360 = 'DV360_Integration',
  IAS = 'IAS',
  neustar = 'Neustar',
  amazonS3 = 'AmazonS3',
  Merkury = 'MerkuryAudiences',
  LiveRamp = 'LiverampFbr',
  AmazonAds = 'AmazonAdAccount',
  MockSocial = 'MockSocial_Account',
}

interface SocialCampaignData {
  connectionId: string;
  extId?: string;
}

interface SocialCampaignConnectionData {
  connections?: SocialCampaignData[];
}

enum SocialAssetType {
  Facebook = 100,
  Youtube = 200,
  LinkedIn = 225,
  GoogleAds = 250,
  TikTok = 300,
  Snapchat = 350,
  TheTradeDesk = 400,
  DoubleVerify = 500,
  Foursquare = 600,
  DV360 = 700,
  IAS = 800,
  Neustar = 900,
  Merkury = 1000,
  LiveRamp = 1100,
  AmazonAds = 1200,
  MockSocial = 1300,
}

enum ExternalPlatform {
  Unknown = 0,
  Facebook = 10,
  Youtube = 20,
  TikTok = 30,
  Pinterest = 40,
  TheTradeDesk = 50,
  GoogleAds = 60,
  DoubleVerify = 70,
  Foursquare = 80,
  DV360 = 90,
  IAS = 100,
  Neustar = 110,
  Merkury = 120,
  LiveRamp = 130,
  Snapchat = 140,
  LinkedIn = 150,
  AmazonAds = 151,
}

enum ConnectionStatus {
  NotTested = 0,
  Success = 1,
  Failed = 2,
  NA = 3,
  Pending = 4,
}

const CONNECTION_STATUS_NAME: Record<ConnectionStatus, string> = {
  [ConnectionStatus.Success]: 'Success',
  [ConnectionStatus.Failed]: 'Failed',
  [ConnectionStatus.NA]: 'NA',
  [ConnectionStatus.NotTested]: 'NotTested',
  [ConnectionStatus.Pending]: 'Pending',
};

interface SocialAdAccountData {
  currency?: string;
  timezone?: string;
  status?: string;
  accessToken?: string;
  expiresIn?: number;
  name?: string;
  refreshToken?: string;
  statusReason?: string;
  pages?: SocialNetworkPage[];
}

interface SocialAdAccount {
  id: number;
  extId: string;
  extName: string;
  connectionStatus: ConnectionStatus;
  connectionStatusName: string;
  connectionFailureReason?: string;
  data: SocialAdAccountData;
  platform: number;
  platformName: SocialPlatform;
  platformUiName: string;
  type: SocialAssetType;
  creationDate?: DateTime;
}

interface RemoteCampaignsAnalyticsData {
  vtc: number;
  ctc: number;
  imps: number;
  clks: number;
  ctr: number;
  campaignId: number;
}

enum SocialPageType {
  FacebookPage = 'facebookPage',
  InstagramAccount = 'instagramAccount',
  TrackingPixel = 'trackingPixel',
  YoutubeChannel = 'youtubeChannel',
  TikTokIdentity = 'tiktokIdentity',
  SnapchatProfile = 'snapchatProfile',
  MockSocialIdentity = 'mockSocialIdentity',
}

interface RemoteCatalogDataProductSets {
  id: string;
  name: string;
  productCount: number;
}

interface RemoteCatalogData extends RemoteCatalogDataProductSets {
  productSets: RemoteCatalogDataProductSets[];
}

interface CatalogData extends CLSelectorOption {
  productSets: CLSelectorOption[];
}

interface RemoteCatalogItem {
  id: string;
  name: string;
  pictureUrl: string;
}

interface RemoteDataFromPreviousCard {
  additionalIdentity: SocialNetworkPage;
  identity: SocialNetworkPage;
  trackingPixel: SocialNetworkPage;
  landingPage: string;
  landingPageSource: LandingPageSource;
}

type DataFromPreviousCard = RemoteDataFromPreviousCard;

type YoutubeAdPropertyConfig = {
  isAllowed: boolean;
  isMandatory: boolean;
  maxLength?: number;
  hasNoCTAOption?: boolean;
};

//social type refactoring

type SocialNetworkPage = {
  id: string;
  name: string;
  type?: string;
  macro?: string;
  iconUrl?: string;
  isFromIdentity?: boolean;
  isFromCampaign?: boolean;
  hasAdditionalIdentity?: boolean;
};

enum SocialTrackingPixelType {
  WebsiteEvents = 'offsite_conversion',
  OfflineEvents = 'offline_conversion',
}

type SocialTrackingPixel = {
  id: string;
  name: string;
  type: SocialTrackingPixelType;
};

type FacebookExternalCampaignPage = {
  facebookPage?: SocialNetworkPage;
  instagramAccount?: SocialNetworkPage;
  trackingPixel?: SocialNetworkPage;
};

type YoutubeExternalCampaignPage = {
  youtubeChannel?: SocialNetworkPage;
};

type TiktokExternalCampaignPage = {
  tiktokIdentity?: SocialNetworkPage;
  trackingPixel?: SocialNetworkPage;
};

type SnapchatExternalCampaignPage = {
  snapchatProfile?: SocialNetworkPage;
};

type LinkedInExternalCampaignPage = {
  linkedinAccount?: SocialNetworkPage;
};

type MockSocialExternalCampaignPage = {
  facebookPage?: SocialNetworkPage;
  instagramAccount?: SocialNetworkPage;
  trackingPixel?: SocialNetworkPage;
};

type SocialExternalCampaignPage =
  | FacebookExternalCampaignPage
  | YoutubeExternalCampaignPage
  | TiktokExternalCampaignPage
  | SnapchatExternalCampaignPage
  | LinkedInExternalCampaignPage
  | MockSocialExternalCampaignPage;

//end of type refactoring

type IdForPreview =
  | {
      adId?: number;
      variationId?: never;
    }
  | {
      adId?: never;
      variationId?: number;
    };

type RemoteLinkedinIdentityData = {
  name: string;
  pictureUrl: string;
};

type LocalLinkedinIdentityData = {
  name: string;
  iconUrl: string;
};

enum SocialAdsetsPageModalAction {
  AddIntegrations = 'AddIntegrations',
  Edit = 'Edit',
  DisconnectIntegrations = 'DisconnectIntegrations',
  SyncAdGroups = 'SyncAdGroups',
  ShowLocation = 'ShowLocation',
  AddEditCustomParams = 'AddEditCustomParams',
  ImportCustomParams = 'ImportCustomParams',
  SummarizeCustomParamsImport = 'SummarizeCustomParamsImport',
}

type SocialAdsetsCustomParam = {
  id: string;
  title: string;
  value: string;
  colName: string;
};

export {
  SocialPlatform,
  SocialCampaignConnectionData,
  SocialAdAccount,
  SocialCampaignData,
  RemoteCampaignsAnalyticsData,
  SocialAssetType,
  ConnectionStatus,
  ExternalPlatform,
  SocialAssetTypeName,
  CONNECTION_STATUS_NAME,
  SocialPageType,
  SocialAdAccountData,
  RemoteCatalogData,
  CatalogData,
  RemoteCatalogItem,
  RemoteDataFromPreviousCard,
  DataFromPreviousCard,
  YoutubeAdPropertyConfig,
  //refactored types
  SocialNetworkPage,
  FacebookExternalCampaignPage,
  YoutubeExternalCampaignPage,
  TiktokExternalCampaignPage,
  SnapchatExternalCampaignPage,
  SocialExternalCampaignPage,
  LinkedInExternalCampaignPage,
  IdForPreview,
  RemoteLinkedinIdentityData,
  LocalLinkedinIdentityData,
  SocialAdsetsPageModalAction,
  SocialAdsetsCustomParam,
  SocialTrackingPixel,
  SocialTrackingPixelType,
  MockSocialExternalCampaignPage,
};
